import * as React from 'react';
import styles from './index.less';
import type { RefSelectProps, SelectProps } from 'antd/lib/select';
import { Select } from 'antd';

/** 与 Figma Polygon 一致：10×6、#222，展开时随 .ant-select-arrow 旋转 */
function FSelect3ArrowIcon() {
  return (
    <svg
      className={styles.arrowSvg}
      width={10}
      height={6}
      viewBox="0 0 10 6"
      aria-hidden
    >
      <path fill="currentColor" d="M0 0l5 6 5-6H0z" />
    </svg>
  );
}

export interface FSelect3Props extends SelectProps {
  /** 上方说明标签（与 Figma「带标题」一致，纵向排在选择器上） */
  label?: React.ReactNode;
  /** 为 true 时标签与选择器同一行（与 Figma「横向标题」一致） */
  labelInline?: boolean;
  /** 选择器下方提示文案（与 Figma「提示文案 / 小标题+提示文案」一致） */
  hint?: React.ReactNode;
  /** 外层包裹容器 className（存在 label / hint 时生效） */
  wrapClassName?: string;
}

function FSelect3(
  {
    label,
    labelInline,
    hint,
    className,
    wrapClassName,
    options,
    classNames: userClassNames,
    suffixIcon,
    loading,
    ...restProps
  }: FSelect3Props,
  ref: React.Ref<RefSelectProps> | undefined,
) {
  const popupRootClass = [
    styles.popupRoot,
    userClassNames?.popup &&
    typeof userClassNames.popup === 'object' &&
    'root' in userClassNames.popup
      ? (userClassNames.popup as { root?: string }).root
      : '',
  ]
    .filter(Boolean)
    .join(' ');

  const hasFrame = label != null || hint != null;

  const selectNode = (
    <Select
      ref={ref}
      {...restProps}
      loading={loading}
      suffixIcon={
        suffixIcon !== undefined ? suffixIcon : loading ? undefined : <FSelect3ArrowIcon />
      }
      options={options?.map((option) => ({
        ...option,
        className: [option.className, styles.optionItem].filter(Boolean).join(' '),
      }))}
      className={[className || '', styles.FSelect3, hasFrame ? styles.triggerFramed : '']
        .filter(Boolean)
        .join(' ')}
      classNames={{
        ...userClassNames,
        popup: {
          ...(typeof userClassNames?.popup === 'object' ? userClassNames.popup : {}),
          root: popupRootClass,
        },
      }}
    />
  );

  if (!hasFrame) {
    return selectNode;
  }

  const showLabel = label != null && label !== '';

  if (labelInline && showLabel) {
    return (
      <div className={[styles.wrap, wrapClassName].filter(Boolean).join(' ')}>
        <div className={styles.inlineRow}>
          <span className={styles.label}>{label}</span>
          <div className={styles.selectFlex}>{selectNode}</div>
        </div>
        {hint != null && hint !== '' && <div className={styles.hint}>{hint}</div>}
      </div>
    );
  }

  return (
    <div className={[styles.wrap, wrapClassName].filter(Boolean).join(' ')}>
      {showLabel && <div className={styles.label}>{label}</div>}
      {selectNode}
      {hint != null && hint !== '' && <div className={styles.hint}>{hint}</div>}
    </div>
  );
}

export default React.forwardRef(FSelect3);
